import { type FC, useCallback, useEffect, useState } from "react";
import { ApolloError } from "@apollo/client";
import { AutoSizer, VirtualTable } from "@snapchat/snap-design-system";
import { SortOrder } from "@snapchat/snap-design-system/es/components/VirtualTable/customPlugins/useSdsSortBy";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { clickableRowCss } from "../../../common/components/table/styles";
import { SearchableCardWithUpsellBanner } from "../../../common/components/SearchableCardWithUpsellBanner";
import { lensStatusLabelMap } from "../../../groups/group-details/constants/lensStatusLabelMap";
import { NoResults } from "../../../groups/components/NoResults";
import { useTableFilterAndSort } from "../../../common/hooks/useTableFilterAndSort";
import { LensListItemModel } from "../../models";
import { getColumns } from "./AllLensesTableColumns";
import { AbsoluteRoute } from "../../../../core/routes";

interface AllLensesTableProps {
    lenses: Readonly<Partial<LensListItemModel>[]>;
    error: ApolloError;
    isLoading: boolean;
    isLastPage: boolean;
    onLoadMore: () => void;
}

const noSearchResultsMessage = "Your search criteria did not match any Lenses.";
const noLensesAvailableMessage = "There are no Lenses available for Camera Kit. Upload new Lenses using Lens Studio.";

export const AllLensesTable: FC<AllLensesTableProps> = ({
    lenses,
    isLoading,
    error,
    isLastPage,
    onLoadMore,
}: AllLensesTableProps) => {
    const navigate = useNavigate();
    const { orgId } = useParams();
    const [searchText, setSearchText] = useState("");

    const getSearchKey = useCallback(
        (lens: Partial<LensListItemModel>) =>
            `${lens.name} ${lens?.compoundId?.id} ${lensStatusLabelMap[lens?.lensStatus ?? ""]?.text} ${lens.scheduleV3Alias}`,
        [],
    );
    const { data, onColumnSort } = useTableFilterAndSort({
        tableData: lenses,
        initialColumn: "updatedAt",
        initialSortOrder: SortOrder.Desc,
        searchText,
        getSearchKey,
    });

    useEffect(() => {
        if (!isLastPage && !isLoading) {
            onLoadMore();
        }
    }, [isLastPage, isLoading, onLoadMore]);

    const onLensClick = useCallback(
        (index: number) => {
            const lens = data[index];
            navigate(
                generatePath(AbsoluteRoute.ORG_LENS_SCHEDULER_LENS, {
                    orgId: orgId ?? "",
                    lensId: lens?.compoundId?.id ?? "",
                    lensSourceType: lens?.compoundId?.lensSourceType ?? "",
                    reference: lens?.compoundId?.reference ?? "",
                }),
            );
        },
        [data, orgId, navigate],
    );

    // TODO: [CAMKIT-1566] - Enable search when working on https://jira.sc-corp.net/browse/CAMKIT-1566
    const onSearch = useCallback((text: string) => setSearchText(text), [setSearchText]);

    const noResultsMessage = searchText ? noSearchResultsMessage : noLensesAvailableMessage;

    return (
        <>
            <SearchableCardWithUpsellBanner
                searchPlaceholder="Search by Lens ID, Name, Status, or Source"
                padBody={false}
                searchValue={searchText}
                onSearch={onSearch}
                isLoading={!isLastPage}
                error={error}
                loadingTooltip="Lenses are still loading."
            >
                <AutoSizer>
                    {({ width, height }: { width: number; height: number }) => (
                        <VirtualTable
                            isLoading={isLoading && data.length === 0}
                            data={data as LensListItemModel[]}
                            columns={getColumns()}
                            hasMore={!isLastPage}
                            width={width}
                            height={height}
                            rowHeight={64}
                            rowClassName={clickableRowCss}
                            emptyMessage={<NoResults description={noResultsMessage} />}
                            loadMore={async () => onLoadMore()}
                            onRowClick={(_, rowIndex: number) => onLensClick(rowIndex)}
                            onColumnSort={onColumnSort}
                        />
                    )}
                </AutoSizer>
            </SearchableCardWithUpsellBanner>
        </>
    );
};
